import type { EEOSettings, OfferStatus } from '@core/offers-types';
import { JobEditorDialogTab } from '@core/offers-types';
import type { JobRequisition } from '@core/requisitions-types';
import type { IconName } from '@core/user-interface-legacy';
import type { Step } from '@core/walkthrough-legacy';

export enum NestedEditor {
  Pipeline = 'pipeline',
  ProfileFields = 'profileFields',
  OpenQuestions = 'openQuestions',
  ReferralQuestions = 'referralQuestions',
  PreferredLocation = 'preferredLocation',
  CandidateInformation = 'candidateInformation',
  TemplateName = 'templateName',
}

export type NestedEditorsActive = Record<NestedEditor, boolean>;

export enum ValidatedJobDetailsField {
  Title = 'title',
  Description = 'description',
  DescriptionJson = 'descriptionJson',
  Requirements = 'requirements',
  EmploymentType = 'employmentType',
  Category = 'category',
  Education = 'education',
  Experience = 'experience',
  LocationIds = 'locationIds',
  WorkModel = 'workModel',
  SalaryMin = 'salary.min',
  SalaryMax = 'salary.max',
  SalaryCurrency = 'salary.currency',
  NumberOfOpeningsEnabled = 'numberOfOpeningsEnabled',
  NumberOfOpenings = 'numberOfOpenings',
}

export enum ValidationMode {
  Basic = 'basic',
  Full = 'full',
}

export type OfferFormValue = { [key: string]: string | null | EEOSettings };

export enum ConfirmableChange {
  Recruiter = 'recruiter',
  HiringManager = 'hiring_manager',
}

export type ChangesToConfirm = Partial<Record<ConfirmableChange, boolean>> | false;

export type JobEditorSidebarItem = {
  label: string;
  icon: IconName;
  path: string;
  isVisible: boolean;
  tabName: JobEditorDialogTab;
  event?: string;
  redirectButtonEvent?: string;
  walkthroughTooltip?: symbol;
  walkthroughTooltipId?: Step['id'];
  hasNewDot?: boolean;
  hasNewBadge?: boolean;
  hasLeadUpgradeIcon?: boolean;
  filledTranslations?: FilledTranslationsData;
};

export type FilledTranslationsData = {
  totalCount: number;
  translatedCount: number;
  langName: string;
};

export type TabRedirectOptionsMap = Record<
  JobEditorDialogTab,
  {
    nextTab?: JobEditorDialogTab;
    previousTab?: JobEditorDialogTab;
  }
>;

export const TabsWithMultilang = <const>[
  JobEditorDialogTab.JobDetails,
  JobEditorDialogTab.Application,
  JobEditorDialogTab.SocialSharing,
];

export type JobSaveStatusChange = OfferStatus.Published | OfferStatus.Internal | false;

export type ActiveFormSaveData = {
  showNotification?: boolean;
  withStatus?: JobSaveStatusChange;
  allowToSkipSave?: boolean;
  isChangingTab?: boolean;
  switchToProsemirror?: boolean;
};

export type JobRequisitionListItem = JobRequisition & {
  departmentName: string;
  jobOpeningsText: string;
  locationText: string;
  statusName: string;
  isAccessible: boolean;
  isUnassignDisabled: boolean;
  isLastRequiredRequisition: boolean;
  hasSimpleOpenings: boolean;
};

export type JobEditorNavigationExtraState =
  | {
      validationFailed?: true;
    }
  | undefined;

export enum VisibilityOptions {
  Visible = 'visible',
  Hidden = 'hidden',
}
